export interface SendVerificationRequest {
  email: string;
  source?: 'coin365' | 'ApexBet';
}

export interface SendVerificationResponse {
  success: boolean;
  message: string;
  data: {
    email: string;
    expiresIn: number;
  };
}

export interface VerifyEmailRequest {
  email: string;
  code: string;
}

export interface VerifyEmailResponse {
  success: boolean;
  message: string;
  data: {
    email: string;
    verifiedAt: string;
  };
}

export interface VerificationStatusResponse {
  success: boolean;
  data: {
    email: string;
    verified: boolean;
    createdAt?: string;
    expiresAt?: string;
  };
}

class EmailVerificationService {
  private baseUrl: string;

  constructor() {
    // Use environment variable or default to localhost
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
  }

  /**
   * Send verification email to user
   */
  async sendVerificationEmail(request: SendVerificationRequest): Promise<SendVerificationResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/email/send-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...request,
          source: request.source || 'ApexBet'
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send verification email');
      }

      return data;
    } catch (error: any) {
      console.error('Error sending verification email:', error);
      // Handle different error types
      if (error instanceof TypeError) {
        throw new Error('Network error. Please check your connection.');
      }
      if (error.message) {
        throw new Error(error.message);
      }
      throw new Error('Failed to send verification email. Please try again.');
    }
  }

  /**
   * Verify email using 6-digit code
   */
  async verifyEmailWithCode(request: VerifyEmailRequest): Promise<VerifyEmailResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/email/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: request.email,
          code: request.code
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to verify email');
      }

      return data;
    } catch (error: any) {
      console.error('Error verifying email with code:', error);
      // Handle different error types
      if (error instanceof TypeError) {
        throw new Error('Network error. Please check your connection.');
      }
      if (error.message) {
        throw new Error(error.message);
      }
      throw new Error('Failed to verify email. Please try again.');
    }
  }

  /**
   * Resend verification email
   */
  async resendVerificationEmail(email: string, source: 'coin365' | 'ApexBet' = 'ApexBet'): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/email/send-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, source }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to resend verification email');
      }

      return data;
    } catch (error: any) {
      console.error('Error resending verification email:', error);
      if (error instanceof TypeError) {
        throw new Error('Network error. Please check your connection.');
      }
      if (error.message) {
        throw new Error(error.message);
      }
      throw new Error('Failed to resend verification email. Please try again.');
    }
  }

  /**
   * Check verification status
   */
  async getVerificationStatus(email: string): Promise<VerificationStatusResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/email/status/${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to check verification status');
      }

      return data;
    } catch (error) {
      console.error('Error checking verification status:', error);
      throw error;
    }
  }

  /**
   * Check if email verification service is available
   */
  async checkServiceHealth(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/health`, {
        method: 'GET',
      });

      return response.ok;
    } catch (error) {
      console.error('Email verification service is not available:', error);
      return false;
    }
  }
}

export const emailVerificationService = new EmailVerificationService();
